import { useEffect, useId, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { normalizeProductionComparisonRows } from "../../util/productionComparison";

const HEIGHT = 520;

const margin = {
  top: 20,
  right: 20,
  bottom: 120,
  left: 90,
};

export default function MonthlyActualsVsPredicted({ portfolioName, data = [], onTooltip }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef([]);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  // Keyboard navigation: which month group is selected and whether arrow-key
  // driven navigation is currently active. Refs (not state) so the d3 handlers
  // can read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  const reactId = useId();
  const idBase = reactId.replace(/:/g, "");
  const actualGradientId = `actualGradient-${idBase}`;
  const predictedGradientId = `predictedGradient-${idBase}`;

  useEffect(() => {
    if (!containerRef.current) return;
    
    const formatNumber = d3.format(",.0f");
    const formatMwh = value => value == null ? '—' : `${formatNumber(value)} MWh`;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Mutable handle to the latest draw cycle's selection helpers. Populated
    // at the end of every `draw()` call so the effect-scoped keyboard/focus
    // listeners (which outlive a single draw) can drive the most recent
    // chart instance.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !dataRef.current) return;

      const width = containerRef.current.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      // Gradient defs (stable ids per component)
      const defs = svg.append("defs");
      const actualGrad = defs
        .append("linearGradient")
        .attr("id", actualGradientId)
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("y2", "1");
      actualGrad.append("stop").attr("offset", "0%").attr("stop-color", "#4472C4");  // blue
      actualGrad.append("stop").attr("offset", "100%").attr("stop-color", "#000");    // black

      const predictedGrad = defs
        .append("linearGradient")
        .attr("id", predictedGradientId)
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("y2", "1");
      predictedGrad.append("stop").attr("offset", "0%").attr("stop-color", "#ED7D31");  // orange
      predictedGrad.append("stop").attr("offset", "100%").attr("stop-color", "#000");    // black

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         SCALES
         ========================= */
      const x0 = d3
        .scaleBand()
        .domain(dataRef.current.map(d => d.date))
        .range([0, innerWidth])
        .padding(0.25);

      const x1 = d3
        .scaleBand()
        .domain(["actual", "predicted"])
        .range([0, x0.bandwidth()])
        .padding(0.2);

      const maxVal = d3.max(
        dataRef.current,
        d => Math.max(d.actual ?? 0, d.predicted ?? 0),
      ) ?? 0;
      const yMax = Math.max(Math.ceil((maxVal * 1.1) / 100) * 100, 100);
      const yTicks = d3.ticks(0, yMax, 6);

      const y = d3
        .scaleLinear()
        .domain([0, yMax])
        .range([innerHeight, 0]);

      /* =========================
         GRIDLINES
         ========================= */
      g.append("g")
        .call(
          d3.axisLeft(y)
            .tickValues(yTicks)
            .tickSize(-innerWidth)
            .tickFormat("")
        )
        .selectAll("line")
        .attr("stroke", "#d9d9d9");

      g.selectAll("path").remove();

      /* =========================
         AXES
         ========================= */
      g.append("g")
        .call(
          d3.axisLeft(y)
            .tickValues(yTicks)
            .tickFormat(d3.format(","))
        );

      g.append("g")
        .attr("transform", `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x0))
        .selectAll("text")
        .attr("transform", "rotate(45)")
        .attr("text-anchor", "start")
        .attr("dx", "0.6em")
        .attr("dy", "0.4em");

      /* =========================
         BARS
         ========================= */
      const monthGroups = g
        .selectAll(".month-group")
        .data(dataRef.current)
        .enter()
        .append("g")
        .attr("class", "month-group")
        .attr("transform", d => `translate(${x0(d.date)},0)`)
        .style("pointer-events", "none");

      const actualFill = `url(#${actualGradientId})`;
      const predictedFill = `url(#${predictedGradientId})`;
      const ACTUAL_COLOR_ACTIVE = "#4472C4";
      const PREDICTED_COLOR_ACTIVE = "#ED7D31";

      // ACTUAL (blue)
      const actualBars = monthGroups
        .append("rect")
        .attr("x", () => x1("actual") + 2)
        .attr("y", d => y(d.actual ?? 0))
        .attr("width", x1.bandwidth())
        .attr("height", d => innerHeight - y(d.actual ?? 0))
        .attr("fill", actualFill);

      // PREDICTED (orange)
      const predictedBars = monthGroups
        .append("rect")
        .attr("x", () => x1("predicted") - 2)
        .attr("y", d => y(d.predicted ?? 0))
        .attr("width", x1.bandwidth())
        .attr("height", d => innerHeight - y(d.predicted ?? 0))
        .attr("fill", predictedFill);

      const resetBars = () => {
        actualBars.attr("fill", actualFill);
        predictedBars.attr("fill", predictedFill);
      };

      /* =========================
         CROSSHAIR + TOOLTIP
         Snap-by-x to nearest month group; tooltip anchors to the top edge of
         the taller bar in the group (centered on the group) rather than
         following the cursor, for stable hover behavior.
         ========================= */
      const gCrosshair = g.append("g").attr("class", "crosshair");
      const gOverlay = g.append("g").attr("class", "overlay");

      const crosshair = gCrosshair.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("opacity", 0)
        .style("pointer-events", "none");

      const centers = dataRef.current.map(d => x0(d.date) + x0.bandwidth() / 2);

      // Anchor the tooltip to the top of the taller bar in the group (the
      // smaller y value), centered on the group. The fallback <div> tooltip
      // uses page coordinates; the page-level callback uses viewport
      // coordinates so it can be positioned by a fixed-position element.
      const showTooltipForDatum = (d, centerX) => {
        const barTopY = Math.min(y(d.actual ?? 0), y(d.predicted ?? 0));
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tip = onTooltipRef.current;
        if (tip) {
          tip({
            visible: true,
            x: rect.left + margin.left + centerX,
            y: rect.top + margin.top + barTopY - 12,
            content: (
              <>
                <strong>Date:</strong> {d.date}
                <br />
                <strong>Actual:</strong> {formatMwh(d.actual)}
                <br />
                <strong>Predicted:</strong> {formatMwh(d.predicted)}
              </>
            ),
          });
        } else if (tooltip) {
          tooltip
            .style("opacity", 1)
            .html(
              `<strong>Date:</strong> ${d.date}<br/>` +
              `<strong>Actual:</strong> ${formatMwh(d.actual)}<br/>` +
              `<strong>Predicted:</strong> ${formatMwh(d.predicted)}`
            )
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + centerX + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + barTopY - 30}px`
            );
        }
      };

      const showSelectionByIndex = (idx) => {
        if (!centers.length) return;
        const clamped = Math.max(0, Math.min(centers.length - 1, idx));
        keyboardIndexRef.current = clamped;
        const d = dataRef.current[clamped];
        if (!d) return;

        crosshair
          .attr("x1", centers[clamped])
          .attr("x2", centers[clamped])
          .style("opacity", 1);

        resetBars();
        actualBars.filter((_, i) => i === clamped).attr("fill", ACTUAL_COLOR_ACTIVE);
        predictedBars.filter((_, i) => i === clamped).attr("fill", PREDICTED_COLOR_ACTIVE);

        showTooltipForDatum(d, centers[clamped]);
      };

      const hideSelection = () => {
        crosshair.style("opacity", 0);
        resetBars();
        const tip = onTooltipRef.current;
        if (tip) {
          tip((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      gOverlay
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove", event => {
          if (!centers.length) return;
          // Any real mouse movement on the chart kicks us out of keyboard
          // navigation mode and back into mouse-follow mode.
          keyboardModeRef.current = false;
          const [mx] = d3.pointer(event);
          const idx = Math.max(
            0,
            Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
          );
          showSelectionByIndex(idx);
        })
        .on("mouseleave", () => {
          // If the chart still has keyboard focus when the mouse leaves,
          // fall back to keyboard-navigation mode so the user can keep
          // arrow-keying without losing the visible selection.
          if (svgRef.current && document.activeElement === svgRef.current) {
            keyboardModeRef.current = true;
            showSelectionByIndex(keyboardIndexRef.current);
          } else {
            hideSelection();
          }
        });

      /* =========================
         Y-AXIS LABEL
         ========================= */
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -70)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Production, MWh");

      /* =========================
         LEGEND
         ========================= */
      const legend = svg.append("g")
        .attr("text-anchor", "start");

      legend.append("rect")
        .attr("x", 0)
        .attr("y", -8)
        .attr("width", 16)
        .attr("height", 12)
        .attr("fill", "#4472C4");

      const actualLabel = legend.append("text")
        .attr("x", 24)
        .attr("y", 2)
        .style("font-weight", "bold")
        .text("Actual (ION delivered), MWh");

      const predictedX = 24 + actualLabel.node().getComputedTextLength() + 40;

      legend.append("rect")
        .attr("x", predictedX)
        .attr("y", -8)
        .attr("width", 16)
        .attr("height", 12)
        .attr("fill", "#ED7D31");

      const predictedLabel = legend.append("text")
        .attr("x", predictedX + 24)
        .attr("y", 2)
        .style("font-weight", "bold")
        .text("Predicted — PVsyst POI (net of all losses), MWh");

      const legendWidth = predictedX + 24 + predictedLabel.node().getComputedTextLength();
      legend.attr("transform", `translate(${(width - legendWidth) / 2}, ${HEIGHT - 35})`);

      // Publish this draw cycle's selection helpers so the effect-scoped
      // keyboard/focus listeners (which outlive a single draw) can drive
      // the most recent chart instance.
      handlersRef.current = {
        centersLength: centers.length,
        showSelectionByIndex,
        hideSelection,
      };

      // If the chart was already focused when this draw ran (e.g. resize
      // fired while the user was navigating with the keyboard), re-render
      // the selection so the crosshair persists across redraws.
      if (
        keyboardModeRef.current &&
        svgRef.current &&
        document.activeElement === svgRef.current
      ) {
        showSelectionByIndex(keyboardIndexRef.current);
      }
    }

    /* =========================
      LOAD API DATA
      ========================= */
    let isMounted = true;

    const load = async () => {
      if (Array.isArray(data) && data.length > 0) {
        dataRef.current = normalizeProductionComparisonRows(data);
        draw();
        return;
      }

      if (!portfolioName) {
        dataRef.current = [];
        draw();
        return;
      }

      try {
        const rows = await apiFetch(`/api/graph/portfolio/${portfolioName}/duel-metrics/last-2-years/production/production-predicted`);

        if (!isMounted) return;
        if (!Array.isArray(rows)) {
          dataRef.current = [];
          draw();
          return;
        }

        dataRef.current = normalizeProductionComparisonRows(rows);

        draw();
      } catch (error) {
        console.error("Failed to load monthly production vs predicted:", error);
        if (!isMounted) return;
        dataRef.current = [];
        draw();
      }
    };

    load();

    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       Arrow-key step through month groups while the <svg> has focus.
       Listeners are attached once per effect run and read from
       `handlersRef` so they always drive the most recent draw cycle.
       ========================= */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!keyboardModeRef.current || !h || !h.centersLength) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) {
          h.showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (keyboardIndexRef.current < h.centersLength - 1) {
          h.showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h || !h.centersLength) return;
      keyboardModeRef.current = true;
      h.showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      handlersRef.current?.hideSelection();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      isMounted = false;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [data, portfolioName, actualGradientId, predictedGradientId]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Monthly actuals vs predicted chart. Use left and right arrow keys to navigate between months."
        style={{
          width: "100%",
          height: HEIGHT,
          display: "block",
        }}
      />
    </div>
  );
}